import { useEffect, useState } from 'react';

import { reportMykissClientError } from '@lib/errors/client';

export interface AsyncQueryResult<T> {
  isLoading: boolean;
  data?: T;
  error?: unknown;
}

/**
 * @param key A string that changes when the inputs change. `null` means "do not fetch".
 * @param fetcher Loads the data for the current key. Aborted when the key changes or on unmount.
 */
export function useAsyncQuery<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
): AsyncQueryResult<T> {
  const [result, setResult] = useState<{ key: string; data?: T; error?: unknown }>();

  useEffect(() => {
    if (key === null) {
      return;
    }
    const controller = new AbortController();
    fetcher(controller.signal)
      .then(data => {
        if (!controller.signal.aborted) {
          setResult({ key, data });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        reportMykissClientError(error);
        setResult({ key, error });
      });
    return () => {
      controller.abort();
    };
    // fetcher is a fresh closure each render; key captures its inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // When a key comes back (pike → perch → pike), its last result shows while the refetch runs
  const settled = key !== null && result?.key === key;
  return {
    isLoading: key !== null && !settled,
    data: settled ? result.data : undefined,
    error: settled ? result.error : undefined,
  };
}
